import React, {useMemo} from 'react';
import {useIntl} from 'react-intl';
import {APPROVAL_STATUS, APPROVAL_TYPE, ApprovalStatus} from '../../../../constants';
import {trackEvent} from './../../../../tracking/amplitude/TrackingV2';
import {MODAL_TYPE, showModal} from '../../../shared/components/modals/generic_modal_conductor';
import Moment from 'moment';
import Util from '../../../shared/util/util';
import {hasFeatureFlag} from '../../../shared/util/FeatureUtil';
import {Button, EMPHASIS} from '@forecast-it/design-system';
import ForecastTooltip from '../../../shared/components/tooltips/ForecastTooltip';
import {createToast} from '../../../shared/components/toasts/another-toast/toaster';
import UpdateApprovalStatusMutation from '../../../../mutations/UpdateApprovalStatusMutation';
import {showApprovalErrorModal} from '../../../../components/timesheets/time-approval/ApprovalErrors';
import {ConfirmationModalPrimaryButton} from '../../../shared/components/modals/general/ConfirmationModal';

const submitTimesheets = ({
	nonViewerPersonSelected,
	selectedPerson,
	isWeekView,
	currentViewingDate,
	lockedDate,
	lastWorkingDayInWeek,
	usingTimeOffApproval,
}) => {
	trackEvent('Submit Timesheets Bottom', 'Clicked', {
		timeRegPerson: nonViewerPersonSelected ? selectedPerson.email : 'myself',
	});
	const today = Moment();
	const submitDate = !isWeekView
		? currentViewingDate
		: today.isSame(currentViewingDate, 'week')
		? today
		: lastWorkingDayInWeek;
	showModal({
		type: MODAL_TYPE.SUBMIT_TIME_APPROVAL,
		selectedPersonName: nonViewerPersonSelected ? selectedPerson.firstName : undefined,
		selectedPersonId: selectedPerson.id,
		currentViewingDate: submitDate,
		lockedDate,
		usingTimeOffApproval,
	});
};

export type TimesheetApprovalActions = {
	showSubmit: boolean;
	showResubmit: boolean;
	showUnsubmit: boolean;
};

/**
 * Decide which approval action the period offers.
 *
 * Under the new statuses the period offers exactly one, and the period's status alone chooses
 * it — so the badge the owner reads and the button they press always name the same next step:
 *
 *   pending             → Submit
 *   submitted           → Unsubmit
 *   declined            → Resubmit
 *   approved, invoiced  → nothing; the period is settled
 *
 * A second button is not needed to reach outstanding time, because the status hierarchy that
 * feeds this ranks a period by what it still asks of its owner rather than by how far it has
 * got (see getApprovalStatusForWeek): a period holding unsubmitted rows reads pending even when
 * some of it has already gone to an approver, so Submit is what it offers. A declined period
 * resolves in two steps — Resubmit sends the declined rows back, after which anything added
 * while it was declined leaves the period reading pending, and Submit picks that up.
 *
 * `hasSubmittableTimeRegs` therefore has no say here; it is read only without the new statuses,
 * where there is no pending status for the hierarchy to rank and the choice stays lock-driven.
 *
 * A company that approves time off but **not** timesheets is a supported configuration — the
 * time-off toggle is not nested under Timesheet approval the way Internal time is — and it gets
 * a narrower answer. None of the timesheet verbs have scope there: project and internal rows are
 * born approved, so Submit and Unsubmit have nothing to act on. Resubmit is kept as the exception.
 * Since PIKA-730 "Submit Time Off Only" does reach a declined leave day, so this is no longer the
 * owner's only route out of a decline — but it is the period-scoped one, and the one whose audit
 * entry names the correction, so it stays offered where the period reads declined.
 *
 * @param periodApprovalStatus  aggregate status of the viewed day or week
 * @param hasSubmittableTimeRegs whether any row in the period is still awaiting submission
 * @param isWorkdaysLocked      whether the submission lock covers the period's working days
 * @param usingTimeApproval     whether the company puts timesheets through approval at all
 */
export const getTimesheetApprovalActions = ({
	periodApprovalStatus,
	hasSubmittableTimeRegs,
	hasNewStatuses,
	isWorkdaysLocked,
	usingTimeApproval,
}: {
	periodApprovalStatus: string | null;
	hasSubmittableTimeRegs: boolean;
	hasNewStatuses: boolean;
	isWorkdaysLocked: boolean;
	usingTimeApproval: boolean;
}): TimesheetApprovalActions => {
	if (!usingTimeApproval) {
		// Time off is the only thing under approval here, and "Submit Time Off Only" already
		// covers sending it on. Resubmit is the one verb that surface cannot stand in for.
		return {
			showSubmit: false,
			showResubmit: hasNewStatuses && periodApprovalStatus === APPROVAL_STATUS.DECLINED,
			showUnsubmit: false,
		};
	}

	if (!hasNewStatuses) {
		// Lock-driven rather than status-driven, as it has always been — but with the same
		// "still something to submit" escape, since a legacy client sees rows written under the
		// new statuses as unsubmitted and needs a way to send them on.
		//
		// No DECLINED case to consider: without the flag the read side projects that status to
		// NOT_SUBMITTED, so it never reaches this branch.
		const showUnsubmit = isWorkdaysLocked;
		return {showSubmit: !showUnsubmit || hasSubmittableTimeRegs, showResubmit: false, showUnsubmit};
	}

	switch (periodApprovalStatus) {
		case APPROVAL_STATUS.APPROVED:
		case APPROVAL_STATUS.INVOICED:
			return {showSubmit: false, showResubmit: false, showUnsubmit: false};
		case APPROVAL_STATUS.DECLINED:
			return {showSubmit: false, showResubmit: true, showUnsubmit: false};
		case APPROVAL_STATUS.SUBMITTED:
			return {showSubmit: false, showResubmit: false, showUnsubmit: true};
		default:
			// Pending, and the legacy unsubmitted status the read side projects onto it.
			return {showSubmit: true, showResubmit: false, showUnsubmit: false};
	}
};

/**
 * Compute the date range a Resubmit click should target.
 *
 * A week with no time registrations at all is settled by a week-scoped person-week-approval
 * row, and the back end's guard for that path only recognises a full-week range — so such a
 * week always operates at week granularity regardless of view mode.
 *
 * Otherwise: Week view = full-week range, Day view = single-day range (partial-day resubmit
 * preserved). The predicate spans every category of time, not just project time: a week
 * holding only internal time or time off has rows for the transition to act on, so it does
 * not need — and should not get — the whole-week widening.
 */
export const getResubmitDateRange = (
	currentViewingDate: Moment.Moment,
	isWeekView: boolean,
	hasTimeRegsInWeek: boolean
): {rangeStart: Moment.Moment; rangeEnd: Moment.Moment} => {
	const useWeekRange = isWeekView || !hasTimeRegsInWeek;
	return {
		rangeStart: useWeekRange ? currentViewingDate.clone().startOf('week') : currentViewingDate.clone(),
		rangeEnd: useWeekRange ? currentViewingDate.clone().endOf('week') : currentViewingDate.clone(),
	};
};

/**
 * The mutation input for a Resubmit click.
 *
 * `includeTimeOff` is not optional here. Internal time is in scope server-side wherever the
 * company approves it, but time off is opt-in per request — omit the flag and declined time off
 * is filtered out of the transition and stays declined however the owner reached this button.
 *
 * Since PIKA-730 it is no longer the *only* thing that can move such a row — "Submit Time Off
 * Only" reaches declined leave too — but Resubmit is the period-scoped verb and the one whose
 * audit entry records a correction rather than a first submission, so a Resubmit click that
 * quietly skipped the leave in the period it names would still be wrong.
 */
export const getResubmitInput = ({
	personId,
	currentViewingDate,
	isWeekView,
	hasTimeRegsInWeek,
}: {
	personId: string;
	currentViewingDate: Moment.Moment;
	isWeekView: boolean;
	hasTimeRegsInWeek: boolean;
}) => {
	const {rangeStart, rangeEnd} = getResubmitDateRange(currentViewingDate, isWeekView, hasTimeRegsInWeek);
	return {
		personIds: [personId],
		approvalType: APPROVAL_TYPE.RESUBMIT,
		includeTimeOff: true,
		startYear: rangeStart.year(),
		startMonth: rangeStart.month() + 1,
		startDay: rangeStart.date(),
		endYear: rangeEnd.year(),
		endMonth: rangeEnd.month() + 1,
		endDay: rangeEnd.date(),
	};
};

/**
 * Which toast a "Submit Time Off Only" click has earned.
 *
 * The button is a backlog sweep, not a period action: `handleSubmitTimeOffFetch`
 * (TimeApprovalLogic.java:146) hardcodes LocalDate.MIN..MAX and the mutation input carries no
 * range, deliberately, so one click clears everything outstanding. The screen, meanwhile, shows
 * one day or one week. Counting the returned rows therefore answers "did the sweep move
 * anything", which is not the question the owner asked — and the two answers come apart in a
 * mundane case: a person with a future PTO booking gets rows back from the sweep while the row
 * they are looking at has not moved. Reporting that as success is how a declined row that the
 * verb cannot touch came to look like a click that silently did nothing.
 *
 * So the period the owner is viewing decides between three honest outcomes: something here moved,
 * something moved but not here, or nothing moved at all.
 *
 * A fourth thing is worth saying in the latter two, and PIKA-730 narrowed it to exactly the case
 * that still needs it. `submittableStatusClause` now admits DECLINED for **time off**, so a
 * declined leave day inside the viewed period is swept by this very click and lands in the
 * success branch above — the hint neither fires nor should. What the sweep still cannot move is
 * declined *project* or *internal* time, which keeps RESUBMIT as its only verb. So when the
 * period offers Resubmit and nothing here moved, the declined time in front of the owner is the
 * kind Resubmit owns, the control that does the job is labelled for timesheets, and nothing else
 * on this surface says so — hence the hint.
 *
 * The hint keys off `offersResubmit` rather than a "has declined time off" predicate, and that
 * now reads as the right question rather than a near-enough proxy for it.
 *
 * @param submittedTimeRegs the mutation payload's `timeRegs` — every row the sweep moved, anywhere
 * @param currentViewingDate the day in view (its week, in Week view)
 * @param isWeekView whether the period is that whole week or the single day
 * @param offersResubmit whether the period is also showing a Resubmit button
 * @returns message ids to render in order, joined by a space
 */
export const getSubmitTimeOffToastMessageIds = ({
	submittedTimeRegs,
	currentViewingDate,
	isWeekView,
	offersResubmit,
}: {
	// Nullable throughout, as the payload types it: a row the graph could not date is simply
	// one this cannot place in the period, and Util.CreateNonUtcMomentDate already says so.
	submittedTimeRegs:
		| ReadonlyArray<{
				year: number | null;
				month: number | null;
				day: number | null;
		  } | null>
		| null
		| undefined;
	currentViewingDate: Moment.Moment;
	isWeekView: boolean;
	offersResubmit: boolean;
}): string[] => {
	const rangeStart = isWeekView ? currentViewingDate.clone().startOf('week') : currentViewingDate.clone().startOf('day');
	const rangeEnd = isWeekView ? currentViewingDate.clone().endOf('week') : currentViewingDate.clone().endOf('day');

	// One walk answers both questions the outcome needs — "did anything move" and "did anything
	// move here" — which matters because the sweep is unbounded and can hand back a backlog.
	let movedAnywhere = false;
	let movedInPeriod = false;
	for (const timeReg of submittedTimeRegs ?? []) {
		if (!timeReg) continue;
		movedAnywhere = true;
		const date = Util.CreateNonUtcMomentDate(timeReg.year, timeReg.month, timeReg.day);
		if (date?.isBetween(rangeStart, rangeEnd, 'day', '[]')) {
			movedInPeriod = true;
			break;
		}
	}

	if (movedInPeriod) return ['time_approval.submit_time_off_toast'];

	const outcome = movedAnywhere
		? 'time_approval.submit_time_off_other_periods_toast'
		: 'time_approval.submit_time_off_nothing_to_submit_toast';

	return offersResubmit ? [outcome, 'time_approval.submit_time_off_resubmit_hint'] : [outcome];
};

const resubmitTimesheets = ({nonViewerPersonSelected, selectedPerson, isWeekView, currentViewingDate, hasTimeRegsInWeek}) => {
	trackEvent('Resubmit Timesheets Bottom', 'Clicked', {
		timeRegPerson: nonViewerPersonSelected ? selectedPerson.email : 'myself',
	});
	Util.CommitMutation(
		UpdateApprovalStatusMutation,
		getResubmitInput({personId: selectedPerson.id, currentViewingDate, isWeekView, hasTimeRegsInWeek}),
		// RESUBMIT is one of the actions the back end rejects without the new-statuses
		// feature flag. It reports that in `errors` rather than by failing, so with no success
		// handler at all the button did nothing and said nothing.
		res => showApprovalErrorModal(res.updateApprovalStatus?.errors)
	);
};

const unsubmitTimesheets = ({
	nonViewerPersonSelected,
	selectedPerson,
	isWeekView,
	currentViewingDate,
	lockedDate,
	companyLockedDate,
}) => {
	trackEvent('Unsubmit Timesheets Bottom', 'Clicked', {
		timeRegPerson: nonViewerPersonSelected ? selectedPerson.email : 'myself',
	});
	const unsubmitDate = !isWeekView ? currentViewingDate : currentViewingDate.clone().startOf('week');
	showModal({
		type: MODAL_TYPE.UNSUBMIT_TIME_APPROVAL,
		selectedPersonName: nonViewerPersonSelected ? selectedPerson.firstName : undefined,
		selectedPersonId: selectedPerson.id,
		currentViewingDate: unsubmitDate,
		companyLockedDate,
		lockedDate,
	});
};

type TimesheetsApprovalButtonsProps = {
	selectedPerson; // TimesheetsPage_viewer['company']['person']
	nonViewerPersonSelected: boolean;
	usingTimeApproval: boolean;
	usingTimeOffApproval: boolean;
	isWeekView: boolean;
	currentViewingDate: Moment.Moment;
	lockedDate?: Moment.Moment;
	companyLockedDate?: Moment.Moment;
	lastWorkingDayInWeek;
	periodTimeApprovalStatus?: ApprovalStatus;
	hasTimeRegsInWeek: boolean;
	hasSubmittableTimeRegsInPeriod: boolean;
};
export const TimesheetsApprovalButtons = ({
	selectedPerson,
	nonViewerPersonSelected,
	usingTimeApproval,
	usingTimeOffApproval,
	isWeekView,
	currentViewingDate,
	lockedDate,
	companyLockedDate,
	lastWorkingDayInWeek,
	periodTimeApprovalStatus,
	hasTimeRegsInWeek,
	hasSubmittableTimeRegsInPeriod,
}: TimesheetsApprovalButtonsProps): JSX.Element | null => {
	const {formatMessage} = useIntl();

	// The id is the Relay global id, as `getResubmitInput` also takes it — the graph runs
	// fromGlobalId on it, so a numeric id would not survive the round trip.
	const handleConfirmTimeOff = (selectedPersonId: string, offersResubmit: boolean) => {
		trackEvent('Submit Time Off Modal Save Button', 'Clicked');
		const onSuccess = res => {
			// A refusal comes back in `errors` with `persons` null — which the unguarded
			// `persons[0]` below used to turn into a TypeError, losing the message entirely.
			if (showApprovalErrorModal(res.updateApprovalStatus?.errors, true)) return;
			const personRes = res.updateApprovalStatus?.persons?.[0];
			if (!personRes) return;
			// Report what the mutation did to the period on screen, not merely that it ran.
			// The sweep is unbounded and the screen is not, so the returned rows have to be
			// read against the viewed period before any of this can be called a success —
			// see getSubmitTimeOffToastMessageIds for why, and for the Resubmit hint.
			//
			// Not flag-gated: which rows are submittable depends on the flag and the category
			// (`submittableStatusClause`, TimeApprovalLogic.java), but the question of whether
			// the sweep reached the period in view is the same either way.
			const messageIds = getSubmitTimeOffToastMessageIds({
				submittedTimeRegs: res.updateApprovalStatus?.timeRegs,
				currentViewingDate,
				isWeekView,
				offersResubmit,
			});
			// "Times offs submitted" is the one outcome that is still a short confirmation, and
			// 2.5s is sized for it. The period-scoped outcomes run to a sentence or two, and the
			// Resubmit hint — the only place the route out of a decline is named — lands last,
			// so those get the 5s the rest of the app uses for multi-clause toasts.
			const isShortConfirmation = messageIds.length === 1 && messageIds[0] === 'time_approval.submit_time_off_toast';
			createToast({
				duration: isShortConfirmation ? 2500 : 5000,
				message: messageIds.map(id => formatMessage({id})).join(' '),
			});
		};

		Util.CommitMutation(
			UpdateApprovalStatusMutation,
			{
				personIds: [selectedPersonId],
				approvalType: APPROVAL_TYPE.SUBMIT_TIME_OFF,
				includeTimeOff: true,
			},
			onSuccess
		);
	};

	const handleCloseTimeOffModal = () => {
		trackEvent('Submit Time Off Modal Cancel Button', 'Clicked');
	};

	// `offersResubmit` rides in from the caller rather than being read off `showResubmit`
	// directly: the action set is resolved further down, after the early return, and the toast
	// only needs to know what the period was offering at the moment of the click.
	const openSubmitTimeOffModal = ({
		nonViewerPersonSelected,
		selectedPerson,
		offersResubmit,
	}: {
		nonViewerPersonSelected: boolean;
		// TimesheetsPage_viewer['company']['person'], same shape the component takes.
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		selectedPerson: any;
		offersResubmit: boolean;
	}) => {
		trackEvent('Submit Time Off Bottom', 'Clicked', {
			timeRegPerson: nonViewerPersonSelected ? selectedPerson.email : 'myself',
		});

		showModal({
			type: MODAL_TYPE.CONFIRMATION,
			title: formatMessage({id: 'time_approval.submit_time_off'}),
			description: formatMessage({id: 'time_off_approval.submit_timesheets_info'}),
			autoClosePositive: true,
			autoCloseNegative: true,
			callbackPositive: () => handleConfirmTimeOff(selectedPerson.id, offersResubmit),
			callbackNegative: handleCloseTimeOffModal,
			includeDefaultMessage: false,
			confirmationModalPrimaryButton: ConfirmationModalPrimaryButton.SAVE,
		});
	};

	const isCompanyLocked = useMemo(() => {
		if (companyLockedDate) {
			return isWeekView
				? companyLockedDate.isSameOrAfter(currentViewingDate.clone().endOf('week'), 'd')
				: companyLockedDate.isSameOrAfter(currentViewingDate, 'd');
		}
		return false;
	}, [isWeekView, companyLockedDate, currentViewingDate]);

	const isWorkdaysLocked = useMemo(() => {
		if (lockedDate && lastWorkingDayInWeek) {
			return lockedDate.isSameOrAfter(lastWorkingDayInWeek, 'd');
		}
		return false;
	}, [isWeekView, lockedDate, lastWorkingDayInWeek]);

	const messageIdSuffix = (usingTimeOffApproval ? '_and_time_off' : '') + '_info';

	if (!usingTimeApproval && !usingTimeOffApproval) return null;

	const hasNewStatuses = hasFeatureFlag('timesheet_approval_new_statuses');

	const {showSubmit, showResubmit, showUnsubmit} = getTimesheetApprovalActions({
		periodApprovalStatus: periodTimeApprovalStatus ?? null,
		hasSubmittableTimeRegs: hasSubmittableTimeRegsInPeriod,
		hasNewStatuses,
		isWorkdaysLocked,
		usingTimeApproval,
	});

	const resubmitButton = (
		<Button
			emphasis={EMPHASIS.HIGH}
			onPress={() =>
				resubmitTimesheets({
					nonViewerPersonSelected,
					selectedPerson,
					isWeekView,
					currentViewingDate,
					hasTimeRegsInWeek,
				})
			}
			data-cy={'resubmit-timesheets-button'}
		>
			{nonViewerPersonSelected
				? formatMessage({id: 'time_approval.resubmit_names_timesheets'}, {name: selectedPerson.firstName})
				: formatMessage({id: 'time_approval.resubmit_timesheets'})}
		</Button>
	);

	return usingTimeApproval ? (
		<>
			{usingTimeOffApproval && (
				<ForecastTooltip
					content={
						nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_off_approval.submit_names_timesheets_info',
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_off_approval.submit_timesheets_info',
							  })
					}
				>
					<Button
						emphasis={EMPHASIS.MONO}
						onPress={() =>
							openSubmitTimeOffModal({
								nonViewerPersonSelected,
								selectedPerson,
								offersResubmit: showResubmit,
							})
						}
						// Same rule as the timesheet Submit below: time off submits through the
						// same mutation branch, which resolves its locked date without a person,
						// so only a company lock can stop it.
						disabled={isCompanyLocked}
						data-cy={'submit-time-off-button'}
					>
						{nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_approval.submit_names_time_off',
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_approval.submit_time_off',
							  })}
					</Button>
				</ForecastTooltip>
			)}
			{showResubmit && resubmitButton}
			{showSubmit && (
				<ForecastTooltip
					content={
						nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_approval.submit_names_timesheets' + messageIdSuffix,
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_approval.submit_timesheets' + messageIdSuffix,
							  })
					}
				>
					<Button
						emphasis={EMPHASIS.HIGH}
						onPress={() =>
							submitTimesheets({
								nonViewerPersonSelected,
								selectedPerson,
								isWeekView,
								currentViewingDate,
								lockedDate,
								lastWorkingDayInWeek,
								usingTimeOffApproval,
							})
						}
						// Only a company lock can stop a submit. The owner's own submit lock must not:
						// it covers precisely the period whose outstanding time this button exists
						// to send on, so gating on it renders the button inert exactly when it is
						// needed. The server agrees — SUBMIT resolves its locked date without a
						// person, so it enforces the company lock alone.
						disabled={isCompanyLocked}
						data-cy={'submit-timesheets-button'}
					>
						{nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_approval.submit_names_timesheet',
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_approval.submit_timesheet',
							  })}
					</Button>
				</ForecastTooltip>
			)}
			{showUnsubmit && (
				// Steps down to secondary on the one path that can still offer two actions at once:
				// without the new statuses the choice is lock-driven, and a locked period that
				// holds unsubmitted rows offers Submit as well.
				<Button
					emphasis={showSubmit ? EMPHASIS.MEDIUM : EMPHASIS.HIGH}
					onPress={() =>
						unsubmitTimesheets({
							nonViewerPersonSelected,
							selectedPerson,
							isWeekView,
							currentViewingDate,
							lockedDate,
							companyLockedDate,
						})
					}
					disabled={isCompanyLocked}
					data-cy={'unsubmit-timesheets-button'}
				>
					{nonViewerPersonSelected
						? formatMessage(
								{
									id: 'time_approval.unsubmit_names_timesheets',
								},
								{
									name: selectedPerson.firstName,
								}
						  )
						: formatMessage({
								id: 'time_approval.unsubmit_timesheets',
						  })}
				</Button>
			)}
		</>
	) : (
		<>
			{showResubmit && resubmitButton}
			{usingTimeOffApproval && (
				<ForecastTooltip
					content={
						nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_off_approval.submit_names_timesheets_info',
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_off_approval.submit_timesheets_info',
							  })
					}
				>
					<Button
						emphasis={EMPHASIS.HIGH}
						onPress={() =>
							openSubmitTimeOffModal({
								nonViewerPersonSelected,
								selectedPerson,
								offersResubmit: showResubmit,
							})
						}
						// Same rule as the timesheet Submit below: time off submits through the
						// same mutation branch, which resolves its locked date without a person,
						// so only a company lock can stop it.
						disabled={isCompanyLocked}
						data-cy={'submit-time-off-button'}
					>
						{nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_approval.submit_names_time_off',
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_approval.submit_time_off',
							  })}
					</Button>
				</ForecastTooltip>
			)}
		</>
	);
};
